import type { DocumentNode, OperationVariables, TypedDocumentNode } from "@apollo/client";
import { useQuery } from "@apollo/client/react";

import { AdditionalHookOptions } from "./types";
import shouldPollHelper from "./shouldPoll";
import usePollingInterval from "./usePollingInterval";
import usePollingManagement from "./usePollingManagement";

const usePolledQuery = <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: Omit<useQuery.Options<NoInfer<TData>, NoInfer<TVariables>>, "returnPartialData"> &
    AdditionalHookOptions<TData>
): useQuery.Result<TData, TVariables, "empty" | "complete" | "streaming"> => {
  const {
    pollInterval: overridePollInterval,
    pollingGroup,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    shouldPoll, // not needed in useLazyQuery
    ...restOptions
  } = options;
  // useQuery's options parameter type is computed via a conditional ({} extends TVariables ? ... : ...)
  // that TS can't resolve against our own unresolved TVariables generic at this call site, even though
  // restOptions always matches whichever branch TVariables ends up in once this hook is instantiated.
  // usePolledQuery never opts into returnPartialData (omitted from the options type above), so the
  // result's dataState is never "partial" - cast to narrow accordingly.
  const queryResult = useQuery(query, restOptions as useQuery.Options<TData, TVariables>) as useQuery.Result<
    TData,
    TVariables,
    "empty" | "complete" | "streaming"
  >;

  const pollInterval = usePollingInterval(query, pollingGroup, overridePollInterval);

  usePollingManagement(
    pollInterval,
    queryResult.startPolling,
    queryResult.stopPolling,
    shouldPollHelper(options, queryResult),
    queryResult.refetch
  );

  return queryResult;
};

export default usePolledQuery;
